/**
 * NewPODrawer - Right-side drawer for creating new Purchase Orders
 * 
 * Reuses the exact same form logic from PurchaseOrders page.
 * Responsive: full-width on mobile, right-side drawer on desktop.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { purchaseOrdersAPI, warehouseAPI, vendorsAPI, crmJobsAPI, poDraftsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { PendingPODocuments } from '../po/PendingPODocuments';
import { PostCreateUploads } from '../po/PostCreateUploads';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchableSelect } from '../ui/searchable-select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { 
  Plus, Loader2, X, DollarSign, Shield, Warehouse, Tag, XCircle, Link2, RotateCcw, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import { ItemSearchCombobox } from '../ItemSearchCombobox';
import { IntegerQuantityInput } from '../IntegerQuantityInput';
import { coerceQty } from '../../lib/qtyUtils';
import { getPendingRequestId, clearPendingRequestId, sendWithRecovery, idempotencyMismatch } from '../../lib/poCreateRecovery';

// --- Draft autosave helpers ---
const DRAFT_DEBOUNCE_MS = 1500;
const getDraftKey = (tenantId, userId) => `po_draft_${tenantId}_${userId}`;

const saveDraft = (tenantId, userId, formData) => {
  try {
    const key = getDraftKey(tenantId, userId);
    localStorage.setItem(key, JSON.stringify({ formData, savedAt: Date.now() }));
  } catch { /* quota exceeded or private mode — silently skip */ }
};

const loadDraft = (tenantId, userId) => {
  try {
    const key = getDraftKey(tenantId, userId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire drafts older than 24 hours
    if (Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.formData;
  } catch { return null; }
};

const clearDraft = (tenantId, userId) => {
  try { localStorage.removeItem(getDraftKey(tenantId, userId)); } catch { /* noop */ }
  clearPendingRequestId(tenantId, userId);
};

// Base statuses for new POs
const BASE_STATUS_OPTIONS = ['Pending', 'Rejected', 'Needs Picked Up', 'Partially Received', 'Pending Assignment', 'Received'];

const NewPODrawer = ({ 
  open, 
  onOpenChange, 
  vendors, 
  departments,
  onPOCreated,
  onVendorAdded,
  // For prefill support
  prefillData = null,
  onPrefillConsumed = () => {},
  // Server draft support
  editingDraft = null,       // PODraftResponse object when editing a server draft
  onDraftSaved = () => {},   // Called after save/update/discard draft
  onDraftSubmitted = () => {},// Called after draft successfully submitted as PO
}) => {
  const { user, hasPerm, hasEntitlement } = useAuth();
  const canSeeCosts = hasPerm('po.approve');
  const hasCrm = hasEntitlement('crm');
  
  const [saving, setSaving] = useState(false);
  const [createRecovery, setCreateRecovery] = useState(null); // null | 'checking' | 'uncertain'
  const [pendingDocs, setPendingDocs] = useState([]);
  const [postCreate, setPostCreate] = useState(null);
  const [uploadState, setUploadState] = useState({ busy: false, failedCount: 0 });
  const [savingDraft, setSavingDraft] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [poPreview, setPoPreview] = useState(null);
  const [showInlineVendorAdd, setShowInlineVendorAdd] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [savingVendor, setSavingVendor] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null); // Tracks server draft being edited
  
  // Conflict resolution state
  const [conflictChoice, setConflictChoice] = useState(null); // null, 'local', 'server'
  const [showConflictBanner, setShowConflictBanner] = useState(false);
  const [conflictLocalTime, setConflictLocalTime] = useState(null);
  const [conflictServerTime, setConflictServerTime] = useState(null);
  
  // Job search state (CRM mode)
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobSearchResults, setJobSearchResults] = useState([]);
  const [jobSearching, setJobSearching] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSearchOpen, setJobSearchOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    warehouse_id: '',
    department_id: '',
    job_id: null,
    job_name: '',
    job_number: '',
    order_description: '',
    line_items: [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
    office_cost: '',
    office_notes: '',
    expected_billable_value: '',
    status: 'Pending',
    is_warranty: false,
  });

  // --- Draft autosave ---
  const draftTimer = useRef(null);
  const draftRestored = useRef(false);
  const tenantId = user?.tenant_id;
  const userId = user?.id;

  // Debounced autosave on every formData change (safety net — works alongside server drafts)
  useEffect(() => {
    if (!open || !tenantId || !userId) return;
    const hasContent = formData.vendor_id || formData.line_items.some(li => (li.item_name || '').trim());
    if (!hasContent) return;

    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      saveDraft(tenantId, userId, formData);
    }, DRAFT_DEBOUNCE_MS);

    return () => { if (draftTimer.current) clearTimeout(draftTimer.current); };
  }, [formData, open, tenantId, userId]);

  // Restore draft when drawer opens — handles 3 scenarios:
  // 1. Editing server draft → load server draft data, check for local conflict
  // 2. No server draft, but local autosave exists → restore local
  // 3. Neither → start fresh
  useEffect(() => {
    if (!open || !tenantId || !userId) return;
    if (prefillData) return; // prefill takes priority over drafts
    if (draftRestored.current) return;

    const localDraft = loadDraft(tenantId, userId);

    if (editingDraft) {
      // Scenario 1: Opening a server draft
      setCurrentDraftId(editingDraft.id);
      const serverData = editingDraft.form_data || {};
      const serverTime = editingDraft.updated_at ? new Date(editingDraft.updated_at).getTime() : 0;

      if (localDraft) {
        const localTime = localDraft._savedAt || Date.now();
        // Check if local version is meaningfully different (and newer)
        if (localTime > serverTime) {
          // Show conflict banner — let user choose
          setConflictLocalTime(localTime);
          setConflictServerTime(serverTime);
          setShowConflictBanner(true);
          // Default to server version until user decides
          setFormData(prev => ({
            ...prev,
            ...serverData,
            line_items: serverData.line_items?.length > 0 ? serverData.line_items : [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
          }));
        } else {
          // Server is newer or same — use server
          setFormData(prev => ({
            ...prev,
            ...serverData,
            line_items: serverData.line_items?.length > 0 ? serverData.line_items : [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
          }));
        }
      } else {
        // No local conflict — just load server draft
        setFormData(prev => ({
          ...prev,
          ...serverData,
          line_items: serverData.line_items?.length > 0 ? serverData.line_items : [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
        }));
      }
      if (serverData.warehouse_id) fetchPoPreview(serverData.warehouse_id);
      draftRestored.current = true;
      if (!localDraft || !(localDraft._savedAt > (editingDraft.updated_at ? new Date(editingDraft.updated_at).getTime() : 0))) {
        toast.info('Server draft loaded', { duration: 3000 });
      }
    } else if (localDraft) {
      // Scenario 2: No server draft, but local autosave exists
      setFormData(prev => ({
        ...prev,
        ...localDraft,
        line_items: localDraft.line_items?.length > 0
          ? localDraft.line_items
          : [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
      }));
      draftRestored.current = true;
      if (localDraft.warehouse_id) fetchPoPreview(localDraft.warehouse_id);
      const itemCount = (localDraft.line_items || []).filter(li => (li.item_name || '').trim()).length;
      toast.info(`Draft restored — ${itemCount} line item${itemCount !== 1 ? 's' : ''} recovered`, {
        icon: <RotateCcw className="w-4 h-4" />,
        duration: 5000,
      });
    }
    // Scenario 3: Neither — start fresh (nothing to do)
  }, [open, tenantId, userId, prefillData, editingDraft]);

  const chooseConflictVersion = (choice) => {
    if (choice === 'local') {
      const localDraft = loadDraft(tenantId, userId);
      if (localDraft) {
        setFormData(prev => ({
          ...prev,
          ...localDraft,
          line_items: localDraft.line_items?.length > 0 ? localDraft.line_items : [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
        }));
        if (localDraft.warehouse_id) fetchPoPreview(localDraft.warehouse_id);
        toast.info('Switched to local version');
      }
    } else {
      toast.info('Keeping server version');
    }
    setShowConflictBanner(false);
    setConflictChoice(choice);
  };

  // Fetch warehouses on mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await warehouseAPI.listWarehouses();
        const activeWarehouses = res.data.filter(w => w.is_active);
        setWarehouses(activeWarehouses);
        // Set default warehouse
        const defaultWarehouse = activeWarehouses.find(w => w.is_default);
        // Only fill the default when nothing is chosen yet (a restored draft may already have a warehouse)
        if (defaultWarehouse) {
          setFormData(prev => {
            if (prev.warehouse_id) return prev;
            fetchPoPreview(defaultWarehouse.id);
            return { ...prev, warehouse_id: defaultWarehouse.id };
          });
        }
      } catch (error) {
        console.error('Failed to fetch warehouses:', error);
      }
    };
    if (open) {
      fetchWarehouses();
    }
  }, [open]);

  // Handle prefill data
  useEffect(() => {
    if (prefillData && open) {
      setFormData(prev => ({
        ...prev,
        vendor_id: prefillData.vendor_id || '',
        job_name: prefillData.job_name || '',
        job_number: prefillData.job_number || '',
        department_id: prefillData.department_id || '',
        warehouse_id: prefillData.warehouse_id || prev.warehouse_id,
        order_description: prefillData.notes || '',
        line_items: prefillData.line_items && prefillData.line_items.length > 0 
          ? prefillData.line_items.map(li => ({
              item_id: li.item_id || null,
              item_name: li.item_name || '',
              quantity: li.quantity || 1,
              unit_cost: li.unit_cost || ''
            }))
          : [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }]
      }));
      if (prefillData.warehouse_id) {
        fetchPoPreview(prefillData.warehouse_id);
      }
      onPrefillConsumed();
      toast.info('Form pre-filled from Part Request');
    }
  }, [prefillData, open, onPrefillConsumed]);

  const fetchPoPreview = useCallback(async (warehouseId) => {
    if (!warehouseId) {
      setPoPreview(null);
      return;
    }
    try {
      const res = await purchaseOrdersAPI.previewNumber(warehouseId);
      setPoPreview(res.data);
    } catch (error) {
      console.error('Failed to fetch PO preview:', error);
      setPoPreview(null);
    }
  }, []);

  const handleWarehouseChange = (warehouseId) => {
    setFormData(prev => ({ ...prev, warehouse_id: warehouseId }));
    fetchPoPreview(warehouseId);
  };

  const resetForm = useCallback(() => {
    const defaultWarehouse = warehouses.find(w => w.is_default);
    setFormData({
      vendor_id: '',
      warehouse_id: defaultWarehouse?.id || '',
      department_id: '',
      job_id: null,
      job_name: '',
      job_number: '',
      order_description: '',
      line_items: [{ item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
      office_cost: '',
      office_notes: '',
      expected_billable_value: '',
      status: 'Pending',
      is_warranty: false,
    });
    setSelectedJob(null);
    setPendingDocs([]);
    setJobSearchQuery('');
    setJobSearchResults([]);
    setCurrentDraftId(null);
    setShowConflictBanner(false);
    setConflictChoice(null);
    draftRestored.current = false;
    if (defaultWarehouse?.id) {
      fetchPoPreview(defaultWarehouse.id);
    } else {
      setPoPreview(null);
    }
  }, [warehouses, fetchPoPreview]);

  // CRM job search
  const searchJobs = useCallback(async (query) => {
    if (!query || query.length < 2) { setJobSearchResults([]); return; }
    setJobSearching(true);
    try {
      const res = await crmJobsAPI.list({ search: query, limit: 10 });
      setJobSearchResults(res.data.jobs || []);
    } catch { setJobSearchResults([]); }
    finally { setJobSearching(false); }
  }, []);

  useEffect(() => {
    if (!hasCrm) return;
    const timer = setTimeout(() => searchJobs(jobSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [jobSearchQuery, searchJobs, hasCrm]);

  // Line item handlers
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { item_id: null, item_name: '', quantity: 1, unit_cost: '' }]
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleItemSelect = (index, selection) => {
    if (!selection) return;
    
    // Normalize selection to support both shapes:
    // - ItemSearchCombobox sends: { item_id, item_name, unit_cost }
    // - Legacy/other callers may send: { id, name, cost }
    const itemId = selection.item_id ?? selection.id ?? null;
    const itemName = selection.item_name ?? selection.name ?? '';
    const unitCost = selection.unit_cost ?? selection.cost ?? null;
    
    // Guard: Don't update state with invalid data
    if (!itemName || typeof itemName !== 'string') {
      console.warn('[handleItemSelect] Invalid selection - missing item name:', selection);
      toast.error("Couldn't select item — please try again");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index 
          ? { 
              ...item, 
              item_id: itemId, 
              item_name: itemName,
              unit_cost: unitCost ?? item.unit_cost
            }
          : item
      )
    }));
  };

  // --- Server-side draft save/update ---
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const payload = { form_data: formData };
      if (currentDraftId) {
        // Update existing draft
        const res = await poDraftsAPI.update(currentDraftId, payload);
        setCurrentDraftId(res.data.id);
        toast.success('Draft updated');
      } else {
        // Create new draft
        const res = await poDraftsAPI.create(payload);
        setCurrentDraftId(res.data.id);
        toast.success('Draft saved');
      }
      clearDraft(tenantId, userId); // Clear local autosave since server copy is now canonical
      onDraftSaved?.();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save draft';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!currentDraftId) return;
    setSavingDraft(true);
    try {
      await poDraftsAPI.delete(currentDraftId);
      clearDraft(tenantId, userId);
      toast.success('Draft discarded');
      resetForm();
      onOpenChange(false);
      onDraftSaved?.();
    } catch (error) {
      toast.error('Failed to discard draft');
    } finally {
      setSavingDraft(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // --- Fix C: Complete frontend validation ---
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (!formData.warehouse_id) {
      toast.error('Please select a warehouse');
      return;
    }
    
    // Safely filter line items - handle null/undefined item_name
    const validLineItems = formData.line_items.filter(li => (li.item_name || '').trim());
    if (validLineItems.length === 0) {
      toast.error('At least one line item is required');
      return;
    }
    
    setSaving(true);
    setCreateRecovery(null);
    try {
      const payload = {
        vendor_id: formData.vendor_id,
        warehouse_id: formData.warehouse_id,
        department_id: formData.department_id === '__none__' ? null : (formData.department_id || null),
        job_id: formData.job_id || null,
        job_name: formData.job_name,
        job_number: formData.job_number,
        order_description: formData.order_description || null,
        line_items: validLineItems.map(li => ({
          item_id: li.item_id || null,
          item_name: (li.item_name || '').trim(),
          quantity: coerceQty(li.quantity) || 1,  // Integer for PO quantities
          unit_cost: li.unit_cost ? parseFloat(li.unit_cost) : null,
        })),
        is_warranty: formData.is_warranty,
      };
      
      if (canSeeCosts) {
        if (formData.office_cost) payload.office_cost = parseFloat(formData.office_cost);
        if (formData.office_notes) payload.office_notes = formData.office_notes;
        if (formData.status) payload.status = formData.status;
        if (formData.expected_billable_value) payload.expected_billable_value = parseFloat(formData.expected_billable_value);
      }
      
      // Same request ID across retries so a timed-out create is never saved twice
      const requestId = getPendingRequestId(tenantId, userId);
      const response = await sendWithRecovery(
        () => purchaseOrdersAPI.create(payload, requestId),
        { onChecking: () => setCreateRecovery('checking') },
      );
      const newPO = response.data;
      setCreateRecovery(null);
      toast.dismiss('po-create-uncertain');
      const warrantyText = formData.is_warranty ? ' (Warranty)' : '';
      
      // --- Clear draft ONLY after confirmed success ---
      clearDraft(tenantId, userId);
      
      // If this was a server draft, delete it
      if (currentDraftId) {
        try { await poDraftsAPI.delete(currentDraftId); } catch { /* draft may already be gone */ }
        onDraftSubmitted?.();
      }
      
      // Check if PO was auto-rejected due to spend limit
      if (newPO.auto_rejected && newPO.status === 'Rejected') {
        // Parse limit from rejection_reason if possible, otherwise show generic message
        const limitMatch = newPO.rejection_reason?.match(/\$[\d,.]+\s*>\s*\$([\d,.]+)/);
        const limitDisplay = limitMatch ? `$${limitMatch[1]}` : 'your spend limit';
        toast.warning(
          `PO ${newPO.po_number} exceeds ${limitDisplay}. It has been marked Rejected and sent to Owners for review.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`PO ${newPO.po_number} created successfully${warrantyText}`);
      }
      
      const docsToUpload = pendingDocs;
      resetForm();
      onPOCreated?.(newPO);
      if (docsToUpload.length) {
        setPostCreate({ po: newPO, files: docsToUpload });
      } else {
        onOpenChange(false);
      }
    } catch (error) {
      const mismatch = idempotencyMismatch(error);
      if (error.uncertain) {
        setCreateRecovery('uncertain');
        toast.warning("We couldn't confirm whether this PO was saved. Your form is kept. Tap \"Check again\" — it will never create a second copy.", { id: 'po-create-uncertain', duration: 10000 });
      } else if (mismatch) {
        setCreateRecovery(null);
        toast.dismiss('po-create-uncertain');
        clearPendingRequestId(tenantId, userId);
        toast.error(mismatch.message, { duration: 12000 });
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error(
          'Request timed out — your data has been saved as a draft. Please check your connection and try again.',
          { duration: 8000 }
        );
      } else if (!error.response) {
        // Network error (no response from server)
        toast.error(
          'Network error — could not reach the server. Your draft has been saved locally. Please check your connection and try again.',
          { duration: 8000 }
        );
      } else {
        // Server responded with an error
        const detail = error.response?.data?.detail;
        const errorMsg = Array.isArray(detail)
          ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ')
          : (detail || 'Failed to create PO');
        if (errorMsg.includes('PO Suffix')) {
          toast.error('Your account is missing a PO Suffix. Ask your admin to assign one in Team settings.');
        } else if (errorMsg.includes('Vendor not found')) {
          toast.error('Selected vendor not found. Please refresh and try again.');
        } else {
          toast.error(errorMsg);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle close - reset form
  const handleOpenChange = (isOpen, force = false) => {
    if (!isOpen && postCreate && uploadState.busy) {
      toast.info('Documents are still uploading. Please wait for them to finish.', { id: 'new-po-upload-guard' });
      return;
    }
    if (!isOpen && postCreate && uploadState.failedCount && !force) {
      toast.warning(`${uploadState.failedCount} document${uploadState.failedCount === 1 ? ' was' : 's were'} not attached. Tap Retry, or "Close without failed files".`, { id: 'new-po-upload-guard' });
      return;
    }
    if (!isOpen && postCreate) {
      onOpenChange(false);
      setTimeout(() => { setPostCreate(null); setUploadState({ busy: false, failedCount: 0 }); }, 350);
      return;
    }
    if (!isOpen) {
      resetForm();
      setShowInlineVendorAdd(false);
      setNewVendorName('');
      draftRestored.current = false;
    }
    onOpenChange(isOpen);
  };

  const handleAddVendor = async (e) => {
    if (e) e.preventDefault();
    if (!newVendorName.trim()) return;
    setSavingVendor(true);
    try {
      const res = await vendorsAPI.create({ name: newVendorName.trim() });
      onVendorAdded?.(res.data);
      // Auto-select new vendor
      setFormData(prev => ({ ...prev, vendor_id: res.data.id }));
      setNewVendorName('');
      setShowInlineVendorAdd(false);
      toast.success('Vendor added');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add vendor';
      if (errorMsg.includes('already exists')) {
        toast.error(`A vendor named "${newVendorName.trim()}" already exists.`);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSavingVendor(false);
    }
  };

  // Calculate PO total
  const poTotal = formData.line_items.reduce((sum, item) => {
    if (item.quantity && item.unit_cost) {
      return sum + (parseInt(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
    }
    return sum;
  }, 0);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl overflow-y-auto"
        data-testid="new-po-drawer"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="text-xl flex items-center gap-2">
            {postCreate ? <Paperclip className="w-5 h-5" /> : currentDraftId ? <RotateCcw className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {postCreate ? 'Attaching Documents' : currentDraftId ? 'Edit Draft' : 'New Purchase Order'}
          </SheetTitle>
          <SheetDescription>
            {postCreate
              ? `Uploading the files you picked to PO ${postCreate.po.po_number}`
              : currentDraftId 
              ? 'Resume editing your saved draft. Submit when ready or update the draft.'
              : 'Fill out the form below to create a new purchase order'}
          </SheetDescription>
          {currentDraftId && !postCreate && (
            <Badge variant="secondary" className="w-fit text-xs mt-1" data-testid="draft-badge">
              Server Draft
            </Badge>
          )}
        </SheetHeader>

        {postCreate ? (
          <PostCreateUploads po={postCreate.po} files={postCreate.files} onStatusChange={setUploadState}
            onClose={() => handleOpenChange(false, true)} />
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PO Details Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">PO Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Employee */}
              <div>
                <Label className="text-muted-foreground text-sm">Employee</Label>
                <p className="font-medium">{user?.name}</p>
              </div>

              {/* Warehouse */}
              <div className="space-y-2">
                <Label>Warehouse *</Label>
                <Select value={formData.warehouse_id} onValueChange={handleWarehouseChange}>
                  <SelectTrigger data-testid="new-po-warehouse-select">
                    <SelectValue placeholder="Select warehouse..." />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>
                        <div className="flex items-center gap-2">
                          <Warehouse className="w-4 h-4 text-muted-foreground" />
                          <span>{w.name}</span>
                          {w.code && <span className="text-xs text-muted-foreground">({w.code})</span>}
                          {w.is_default && <Badge variant="outline" className="text-xs ml-1">Default</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* PO Number Preview */}
              <div>
                <Label className="text-muted-foreground text-sm flex items-center gap-2">
                  PO Number
                  <span className="text-xs font-normal bg-muted px-1.5 py-0.5 rounded">Auto-generated</span>
                </Label>
                {poPreview?.preview ? (
                  <p className="font-mono font-semibold text-lg text-primary bg-muted/50 px-2 py-1 rounded inline-block mt-1">
                    {poPreview.preview}
                  </p>
                ) : formData.warehouse_id ? (
                  <p className="text-sm text-amber-600 mt-1">Warehouse code not set.</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic mt-1">Select a warehouse to preview</p>
                )}
              </div>

              {/* Vendor */}
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      options={vendors.map(v => ({ value: v.id, label: v.name }))}
                      value={formData.vendor_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, vendor_id: value }))}
                      placeholder="Select vendor..."
                      searchPlaceholder="Search vendors..."
                      emptyText="No vendors found."
                      data-testid="new-po-vendor-select"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => { setShowInlineVendorAdd(v => !v); setNewVendorName(''); }}
                    data-testid="new-po-add-vendor-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.vendor_id && (() => {
                  const v = vendors.find(v => v.id === formData.vendor_id);
                  return v?.payment_terms ? (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="po-vendor-payment-terms">
                      Payment Terms: <span className="font-medium text-foreground">{v.payment_terms}</span>
                    </p>
                  ) : null;
                })()}
                {showInlineVendorAdd && (
                  <div className="flex gap-2 items-end mt-2 p-3 bg-muted/50 rounded-lg border" data-testid="new-po-inline-vendor-form">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">New Vendor Name</Label>
                      <Input
                        value={newVendorName}
                        onChange={(e) => setNewVendorName(e.target.value)}
                        placeholder="Enter vendor name"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddVendor(e); } }}
                        autoFocus
                        data-testid="new-po-new-vendor-input"
                      />
                    </div>
                    <Button type="button" size="sm" disabled={savingVendor || !newVendorName.trim()} onClick={handleAddVendor} data-testid="new-po-save-vendor-btn">
                      {savingVendor && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      Add
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setShowInlineVendorAdd(false); setNewVendorName(''); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              {/* Job Reference - Smart selector based on CRM availability */}
              {hasCrm ? (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" />
                    Link to CRM Job
                  </Label>
                  {selectedJob ? (
                    <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30" data-testid="new-po-selected-job">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{selectedJob.job_name || selectedJob.job_number}</p>
                        <p className="text-xs text-muted-foreground">#{selectedJob.job_number}</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        setSelectedJob(null);
                        setFormData(prev => ({ ...prev, job_id: null, job_name: '', job_number: '' }));
                      }}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Input
                        value={jobSearchQuery}
                        onChange={(e) => { setJobSearchQuery(e.target.value); setJobSearchOpen(true); }}
                        onFocus={() => setJobSearchOpen(true)}
                        placeholder="Search CRM jobs..."
                        data-testid="new-po-job-search"
                      />
                      {jobSearching && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />}
                      {jobSearchOpen && jobSearchResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="new-po-job-results">
                          {jobSearchResults.map(j => (
                            <button
                              key={j.id}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                              onClick={() => {
                                setSelectedJob(j);
                                setFormData(prev => ({
                                  ...prev,
                                  job_id: j.id,
                                  job_name: j.job_name || '',
                                  job_number: j.job_number || '',
                                }));
                                setJobSearchOpen(false);
                                setJobSearchQuery('');
                              }}
                              data-testid={`new-po-job-option-${j.id}`}
                            >
                              <span className="font-medium">{j.job_name || j.job_number}</span>
                              <span className="text-muted-foreground ml-2">#{j.job_number}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Or enter text-only below if no CRM job exists yet</p>
                </div>
              ) : null}

              {/* Job Name (always available for text-only fallback) */}
              <div className="space-y-2">
                <Label>{hasCrm ? 'Job Name (text fallback)' : 'Job Name'}</Label>
                <Input
                  value={formData.job_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, job_name: e.target.value }))}
                  placeholder="Enter job name (optional)"
                  disabled={!!selectedJob}
                  data-testid="new-po-job-name"
                />
              </div>

              {/* Job Number (always available for text-only fallback) */}
              <div className="space-y-2">
                <Label>{hasCrm ? 'Job # (text fallback)' : 'Job Number'}</Label>
                <Input
                  value={formData.job_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, job_number: e.target.value }))}
                  placeholder="Optional"
                  disabled={!!selectedJob}
                  data-testid="new-po-job-number"
                />
              </div>

              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={formData.department_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, department_id: value }))}
                >
                  <SelectTrigger data-testid="new-po-department-select">
                    <SelectValue placeholder="Select department (optional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          <span>{d.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Order Description */}
              <div className="space-y-2">
                <Label>Order Description</Label>
                <Textarea
                  value={formData.order_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_description: e.target.value }))}
                  placeholder="Optional notes about this order..."
                  rows={2}
                  data-testid="new-po-description"
                />
              </div>
            </CardContent>
          </Card>

          {/* Line Items Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">Line Items</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Header */}
                <div className="flex gap-2 items-center text-xs text-muted-foreground font-medium">
                  <div className="flex-1 min-w-0">Item Name</div>
                  <div className="w-16 flex-shrink-0 text-center">Qty</div>
                  {canSeeCosts && <div className="w-20 flex-shrink-0 text-center">Cost</div>}
                  <div className="w-8"></div>
                </div>
                {/* Items */}
                {formData.line_items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <div className="flex-1 min-w-0">
                      <ItemSearchCombobox
                        value={item.item_name}
                        itemId={item.item_id}
                        onChange={(value) => updateLineItem(index, 'item_name', value)}
                        onSelect={(selection) => handleItemSelect(index, selection)}
                        placeholder="Search parts or type new..."
                        data-testid={`new-po-line-item-${index}`}
                      />
                    </div>
                    <div className="w-20 flex-shrink-0">
                      <IntegerQuantityInput
                        min={1}
                        value={item.quantity}
                        onChange={(val) => updateLineItem(index, 'quantity', val)}
                        className="text-center"
                        data-testid={`new-po-line-qty-${index}`}
                      />
                    </div>
                    {canSeeCosts && (
                      <div className="w-20 flex-shrink-0">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_cost}
                          onChange={(e) => updateLineItem(index, 'unit_cost', e.target.value)}
                          placeholder="$0.00"
                          className="text-right text-sm"
                        />
                      </div>
                    )}
                    {formData.line_items.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        className="w-8 h-8 text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    ) : <div className="w-8" />}
                  </div>
                ))}
                {formData.line_items.every(li => !(li.item_name || '').trim()) && (
                  <p className="text-sm text-amber-600">At least one item name is required</p>
                )}
                {/* Total */}
                {canSeeCosts && poTotal > 0 && (
                  <div className="flex justify-end items-center gap-2 pt-3 border-t mt-3">
                    <span className="text-sm font-medium text-muted-foreground">Total:</span>
                    <span className="text-lg font-bold text-primary">${poTotal.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Office Use Only */}
          {canSeeCosts && (
            <Card className="border-dashed border-primary/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Office Use Only
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Order Cost ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.office_cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, office_cost: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expected Billable ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.expected_billable_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, expected_billable_value: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Receipt Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BASE_STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Office Notes</Label>
                  <Textarea
                    value={formData.office_notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, office_notes: e.target.value }))}
                    placeholder="Internal notes..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          <PendingPODocuments files={pendingDocs} onChange={setPendingDocs} />

          {/* Warranty Checkbox */}
          <div className="flex items-start space-x-3 p-4 rounded-lg bg-purple-50 border border-purple-200">
            <Checkbox
              id="new-po-warranty"
              checked={formData.is_warranty}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_warranty: checked }))}
              data-testid="new-po-warranty-checkbox"
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="new-po-warranty"
                className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
              >
                <Shield className="w-4 h-4 text-purple-600" />
                This is a warranty replacement part
              </label>
              <p className="text-xs text-purple-700/80">
                Check if ordering a part under manufacturer warranty.
              </p>
            </div>
          </div>

          {/* Submit */}
          {/* Conflict resolution banner */}
          {showConflictBanner && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-2" data-testid="draft-conflict-banner">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1.5">
                A newer local version was found
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                Local: {conflictLocalTime ? new Date(conflictLocalTime).toLocaleString() : 'Unknown'}
                {' · '}
                Server: {conflictServerTime ? new Date(conflictServerTime).toLocaleString() : 'Unknown'}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => chooseConflictVersion('local')} data-testid="conflict-use-local">
                  Use Local
                </Button>
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => chooseConflictVersion('server')} data-testid="conflict-use-server">
                  Keep Server
                </Button>
              </div>
            </div>
          )}

          {createRecovery === 'uncertain' && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="po-create-uncertain-banner">
              We couldn't confirm whether this PO was saved (the connection dropped or timed out). Your form is kept.
              Tap <span className="font-medium">Check again</span>: if the PO was saved you'll get it back, and it will never be created twice.
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-4 border-t">
            <div className="flex gap-2">
              {currentDraftId && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs"
                  onClick={handleDiscardDraft}
                  disabled={savingDraft || saving}
                  data-testid="discard-draft-btn"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Discard
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                type="button"
                variant="secondary"
                onClick={handleSaveDraft}
                disabled={savingDraft || saving}
                data-testid="save-draft-btn"
              >
                {savingDraft && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {currentDraftId ? 'Update Draft' : 'Save as Draft'}
              </Button>
              <Button 
                type="submit" 
                disabled={saving || savingDraft || !formData.vendor_id || !formData.warehouse_id}
                data-testid="new-po-submit-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {createRecovery === 'checking' ? 'Checking whether PO was saved…'
                  : createRecovery === 'uncertain' ? 'Check again'
                  : formData.is_warranty ? 'Submit Warranty PO' : 'Submit PO'}
              </Button>
            </div>
          </div>
        </form>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default NewPODrawer;
